import type { KeyboardEvent, ReactNode } from "react";
import { Search } from "lucide-react";
import { cn } from "@/lib/utils";

export function Toolbar({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div
      className={cn(
        "flex gap-2 items-center px-4 py-3 border-b border-[#f2f4f7] flex-wrap",
        className,
      )}
    >
      {children}
    </div>
  );
}

export function ToolbarSearch({
  placeholder = "Search…",
  width = 280,
  value,
  onChange,
  onKeyDown,
}: {
  placeholder?: string;
  width?: number;
  value?: string;
  onChange?: (v: string) => void;
  onKeyDown?: (event: KeyboardEvent<HTMLInputElement>) => void;
}) {
  return (
    <div
      className="h-8 bg-[#f7f7f7] border border-[#e8ebf0] rounded-md flex items-center px-2.5 gap-2 text-xs text-[#7b8799]"
      style={{ flex: `0 1 ${width}px` }}
    >
      <Search className="w-3 h-3 shrink-0" />
      <input
        type="text"
        className="border-0 outline-0 bg-transparent flex-1 font-[inherit] text-xs text-[#08254c] placeholder:text-[#7b8799] min-w-0"
        placeholder={placeholder}
        value={value ?? ""}
        onChange={(e) => onChange?.(e.target.value)}
        onKeyDown={onKeyDown}
      />
    </div>
  );
}

export function ToolbarChip({
  children,
  active,
  onClick,
  icon,
  disabled,
}: {
  children: ReactNode;
  active?: boolean;
  onClick?: () => void;
  icon?: ReactNode;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-pressed={active}
      className={cn(
        "h-7 px-2.5 rounded-md border font-mono text-[11px] tracking-[0.04em] cursor-pointer inline-flex items-center gap-1.5 transition-colors disabled:cursor-not-allowed disabled:opacity-40",
        active
          ? "bg-[#08254c] text-white border-[#08254c]"
          : "bg-white text-[#596b85] border-[#e8ebf0] hover:border-[#08254c] hover:text-[#08254c]",
      )}
    >
      {icon && <span className="[&_svg]:w-3 [&_svg]:h-3">{icon}</span>}
      {children}
    </button>
  );
}

export function ToolbarSpacer() {
  return <div className="flex-1" />;
}

export function Legend({ chips }: { chips: { label: string; color: string; glow?: boolean; border?: string }[] }) {
  return (
    <div className="flex gap-4 text-[11px] text-[#596b85] font-mono tracking-[0.04em] items-center flex-wrap">
      {chips.map((c) => (
        <span key={c.label} className="flex items-center gap-1.5">
          <span
            className="w-2 h-2 rounded-full"
            style={{
              background: c.color,
              boxShadow: c.glow ? `0 0 6px ${c.color}` : undefined,
              border: c.border,
            }}
          />
          {c.label}
        </span>
      ))}
    </div>
  );
}
